import { useState } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Platform,
  Organization,
  PLATFORM_TYPES,
  PLATFORM_REQUIRED_SCOPES,
  listPlatforms,
  createPlatform,
  updatePlatform,
  testPlatformConnection,
  deletePlatform,
  listOrganizations,
  updatePlatformDNSDefaults,
  updatePlatformNotificationEmail,
} from '../api/client'
import { useAuth } from '../auth/AuthContext'

const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }
const btnStyle: React.CSSProperties = { padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }
const inputSt: React.CSSProperties = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }

export default function Platforms() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { data: platforms = [] } = useQuery<Platform[]>({ queryKey: ['platforms'], queryFn: listPlatforms })
  const { data: orgs = [] } = useQuery<Organization[]>({ queryKey: ['organizations'], queryFn: listOrganizations })

  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [platformType, setPlatformType] = useState<string>(PLATFORM_TYPES[0])
  const [serverFQDN, setServerFQDN] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [orgID, setOrgID] = useState<number | ''>('')

  // A successful connection test is required before Create Platform is
  // enabled — editing the server/key after testing invalidates it, since
  // the tested values no longer necessarily match what would be saved.
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle')
  const [testError, setTestError] = useState<string | null>(null)

  const invalidateTest = () => { setTestStatus('idle'); setTestError(null) }

  const testMut = useMutation({
    mutationFn: () => testPlatformConnection(serverFQDN, apiKey),
    onMutate: () => { setTestStatus('testing'); setTestError(null) },
    onSuccess: () => setTestStatus('success'),
    onError: (e: unknown) => {
      setTestStatus('error')
      const msg = axios.isAxiosError(e) ? (e.response?.data?.error ?? e.message) : String(e)
      setTestError(msg)
    },
  })

  const createMut = useMutation({
    mutationFn: () => createPlatform({ orgID: orgID === '' ? undefined : orgID, name, platformType, serverFQDN, apiKey }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platforms'] })
      setName('')
      setServerFQDN('')
      setApiKey('')
      setShowCreate(false)
      invalidateTest()
    },
  })
  const deleteMut = useMutation({
    mutationFn: (id: number) => deletePlatform(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platforms'] }),
  })

  const orgName = (id: number) => orgs.find(o => o.ID === id)?.Name ?? `#${id}`
  const canManage = user?.IsSuperUser || user?.CanManagePlatforms

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Platforms</h3>
        {canManage && (
          <button style={{ ...btnStyle, background: '#2563eb', color: '#fff' }} onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? 'Cancel' : '+ Add Platform'}
          </button>
        )}
      </div>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px', maxWidth: '70ch' }}>
        A platform is a UCaaS integration (e.g. netsapiens). Link an org to a platform and reseller
        from the Organizations page, then a device under that org can pick a live-fetched customer
        domain to sync call queues from.
      </p>

      {showCreate && (
        <div style={{ ...cardStyle, borderColor: '#2563eb' }}>
          <h4 style={{ margin: '0 0 12px' }}>New Platform</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Name</label>
              <input style={{ ...inputSt, width: '100%' }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. ConnectWare APAC" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Platform Type</label>
              <select style={{ ...inputSt, width: '100%' }} value={platformType} onChange={e => setPlatformType(e.target.value)}>
                {PLATFORM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Server FQDN</label>
              <input
                style={{ ...inputSt, width: '100%' }}
                value={serverFQDN}
                onChange={e => { setServerFQDN(e.target.value); invalidateTest() }}
                placeholder="e.g. core1-nj.netsapiens.com"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>API Key</label>
              <input style={{ ...inputSt, width: '100%' }} value={apiKey} onChange={e => { setApiKey(e.target.value); invalidateTest() }} />
              <p style={{ margin: '4px 0 0', fontSize: 11.5, color: '#9ca3af' }}>
                Use a read-only key scoped to: <code>{PLATFORM_REQUIRED_SCOPES[platformType] ?? 'reseller,domain,agent,callqueue,device'}</code>
              </p>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Organization (blank = your own)</label>
              <select style={{ ...inputSt, width: '100%' }} value={orgID} onChange={e => setOrgID(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">— your org —</option>
                {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <button
              style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
              disabled={!serverFQDN.trim() || !apiKey.trim() || testMut.isPending}
              onClick={() => testMut.mutate()}
            >
              {testStatus === 'testing' ? 'Testing…' : 'Test Connection'}
            </button>
            {testStatus === 'success' && (
              <span style={{ fontSize: 13, color: '#065f46', fontWeight: 600 }}>✓ Connected</span>
            )}
            {testStatus === 'error' && (
              <span style={{ fontSize: 13, color: '#991b1b' }}>{testError}</span>
            )}
          </div>

          <button
            style={{ ...btnStyle, background: testStatus === 'success' ? '#2563eb' : '#93c5fd', color: '#fff', cursor: testStatus === 'success' ? 'pointer' : 'not-allowed' }}
            disabled={!name.trim() || !platformType.trim() || testStatus !== 'success' || createMut.isPending}
            onClick={() => createMut.mutate()}
            title={testStatus !== 'success' ? 'Test the connection successfully first' : undefined}
          >
            {createMut.isPending ? 'Creating…' : 'Create Platform'}
          </button>
        </div>
      )}

      {platforms.length === 0 && (
        <div style={cardStyle}><p style={{ color: '#9ca3af', margin: 0 }}>No platforms configured yet.</p></div>
      )}
      {platforms.map(p => (
        <PlatformCard key={p.ID} platform={p} orgName={orgName} canManage={!!canManage} onDelete={() => deleteMut.mutate(p.ID)} />
      ))}
    </div>
  )
}

function PlatformCard({
  platform, orgName, canManage, onDelete,
}: { platform: Platform; orgName: (id: number) => string; canManage: boolean; onDelete: () => void }) {
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <h4 style={{ margin: 0 }}>{platform.Name}</h4>
          <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#6b7280' }}>
            {platform.PlatformType} · {platform.ServerFQDN || 'no server configured'} · owned by {orgName(platform.OrgID)}
          </p>
        </div>
        {canManage && (
          <button style={{ ...btnStyle, background: '#fee2e2', color: '#991b1b' }} onClick={onDelete}>Delete</button>
        )}
      </div>

      <EditConnectionSection platform={platform} canManage={canManage} />
      <DNSDefaultsSection platform={platform} canManage={canManage} />
      <NotificationEmailSection platform={platform} canManage={canManage} />
    </div>
  )
}

// EditConnectionSection lets an already-created platform's name/type/
// server/key be changed. Only re-tests the connection (same gate as
// createPlatform) when the server or key actually changed — a plain
// rename shouldn't require re-proving the credentials still work.
function EditConnectionSection({ platform, canManage }: { platform: Platform; canManage: boolean }) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [name, setName] = useState(platform.Name)
  const [platformType, setPlatformType] = useState(platform.PlatformType)
  const [serverFQDN, setServerFQDN] = useState(platform.ServerFQDN)
  const [apiKey, setApiKey] = useState(platform.APIKey)
  const [saved, setSaved] = useState(false)

  const connectionChanged = serverFQDN !== platform.ServerFQDN || apiKey !== platform.APIKey
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle')
  const [testError, setTestError] = useState<string | null>(null)
  const invalidateTest = () => { setTestStatus('idle'); setTestError(null) }

  const testMut = useMutation({
    mutationFn: () => testPlatformConnection(serverFQDN, apiKey),
    onMutate: () => { setTestStatus('testing'); setTestError(null) },
    onSuccess: () => setTestStatus('success'),
    onError: (e: unknown) => {
      setTestStatus('error')
      const msg = axios.isAxiosError(e) ? (e.response?.data?.error ?? e.message) : String(e)
      setTestError(msg)
    },
  })

  const saveMut = useMutation({
    mutationFn: () => updatePlatform(platform.ID, { name, platformType, serverFQDN, apiKey }),
    onSuccess: () => {
      setSaved(true)
      invalidateTest()
      qc.invalidateQueries({ queryKey: ['platforms'] })
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const canSave = name.trim() && platformType.trim() && serverFQDN.trim() && apiKey.trim()
    && (!connectionChanged || testStatus === 'success')

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? '▾' : '▸'} Edit Connection
      </button>
      {expanded && (
        <div style={{ marginTop: 8, marginLeft: 12, maxWidth: 480 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Name</label>
          <input
            style={{ ...inputSt, width: '100%', marginBottom: 10 }}
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={!canManage}
          />
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Platform Type</label>
          <select
            style={{ ...inputSt, width: '100%', marginBottom: 10 }}
            value={platformType}
            onChange={e => setPlatformType(e.target.value)}
            disabled={!canManage}
          >
            {PLATFORM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Server FQDN</label>
          <input
            style={{ ...inputSt, width: '100%', marginBottom: 10 }}
            value={serverFQDN}
            onChange={e => { setServerFQDN(e.target.value); invalidateTest() }}
            disabled={!canManage}
          />
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>API Key</label>
          <input
            style={{ ...inputSt, width: '100%', marginBottom: 10 }}
            value={apiKey}
            onChange={e => { setApiKey(e.target.value); invalidateTest() }}
            disabled={!canManage}
          />
          {canManage && (
            <>
              {connectionChanged && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <button
                    style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
                    disabled={!serverFQDN.trim() || !apiKey.trim() || testMut.isPending}
                    onClick={() => testMut.mutate()}
                  >
                    {testStatus === 'testing' ? 'Testing…' : 'Test Connection'}
                  </button>
                  {testStatus === 'success' && <span style={{ fontSize: 13, color: '#065f46', fontWeight: 600 }}>✓ Connected</span>}
                  {testStatus === 'error' && <span style={{ fontSize: 13, color: '#991b1b' }}>{testError}</span>}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  style={{ ...btnStyle, background: canSave ? '#2563eb' : '#93c5fd', color: '#fff', cursor: canSave ? 'pointer' : 'not-allowed' }}
                  onClick={() => saveMut.mutate()}
                  disabled={!canSave || saveMut.isPending}
                  title={connectionChanged && testStatus !== 'success' ? 'Test the new connection successfully first' : undefined}
                >
                  {saveMut.isPending ? 'Saving…' : 'Save'}
                </button>
                {saved && <span style={{ fontSize: 12.5, color: '#065f46' }}>Saved.</span>}
                {saveMut.isError && <span style={{ fontSize: 12.5, color: '#dc2626' }}>Save failed.</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function DNSDefaultsSection({ platform, canManage }: { platform: Platform; canManage: boolean }) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [fqdn, setFqdn] = useState(platform.DNSFailoverFQDN ?? '')
  const [resolversText, setResolversText] = useState((platform.DNSUpstreamResolvers ?? []).join(', '))
  const [saved, setSaved] = useState(false)

  const saveMut = useMutation({
    mutationFn: () => updatePlatformDNSDefaults(platform.ID, {
      FQDN: fqdn.trim(),
      UpstreamResolvers: resolversText.split(',').map(s => s.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['platforms'] })
      setTimeout(() => setSaved(false), 3000)
    },
  })

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? '▾' : '▸'} DNS Failover Defaults{platform.DNSFailoverFQDN ? `: ${platform.DNSFailoverFQDN}` : ' (none set)'}
      </button>
      {expanded && (
        <div style={{ marginTop: 8, marginLeft: 12, maxWidth: 480 }}>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
            Every device linked to a domain under this platform inherits this hostname and
            resolver list for its DNS Failover service, unless the device sets its own
            (device settings always win).
          </p>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Hostname</label>
          <input
            style={{ ...inputSt, width: '100%', marginBottom: 10 }}
            placeholder="localfailover.example.com"
            value={fqdn}
            onChange={e => setFqdn(e.target.value)}
            disabled={!canManage}
          />
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Upstream Resolvers (comma-separated)
          </label>
          <input
            style={{ ...inputSt, width: '100%', marginBottom: 10 }}
            placeholder="1.1.1.1, 1.0.0.1"
            value={resolversText}
            onChange={e => setResolversText(e.target.value)}
            disabled={!canManage}
          />
          {canManage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
                onClick={() => saveMut.mutate()}
                disabled={saveMut.isPending}
              >
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </button>
              {saved && <span style={{ fontSize: 12.5, color: '#065f46' }}>Saved.</span>}
              {saveMut.isError && <span style={{ fontSize: 12.5, color: '#dc2626' }}>Save failed.</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function NotificationEmailSection({ platform, canManage }: { platform: Platform; canManage: boolean }) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [email, setEmail] = useState(platform.NotificationEmail ?? '')
  const [saved, setSaved] = useState(false)

  const saveMut = useMutation({
    mutationFn: () => updatePlatformNotificationEmail(platform.ID, email.trim()),
    onSuccess: () => {
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['platforms'] })
      setTimeout(() => setSaved(false), 3000)
    },
  })

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? '▾' : '▸'} Notification Email{platform.NotificationEmail ? `: ${platform.NotificationEmail}` : ' (none set)'}
      </button>
      {expanded && (
        <div style={{ marginTop: 8, marginLeft: 12, maxWidth: 480 }}>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
            Sent here if this platform's periodic ring-group/credential sync fails — e.g. a
            revoked or expired API key. Existing data is never changed on a failed sync;
            this is just so you find out rather than noticing devices going stale.
          </p>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Email</label>
          <input
            style={{ ...inputSt, width: '100%', marginBottom: 10 }}
            placeholder="ops@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={!canManage}
          />
          {canManage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
                onClick={() => saveMut.mutate()}
                disabled={saveMut.isPending}
              >
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </button>
              {saved && <span style={{ fontSize: 12.5, color: '#065f46' }}>Saved.</span>}
              {saveMut.isError && <span style={{ fontSize: 12.5, color: '#dc2626' }}>Save failed.</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

